import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity, AlertTriangle, DollarSign, Package, Shield, Wrench, Heart, Code2, FlaskConical, ArrowDownUp,
} from 'lucide-react';
import { getDailyReport, getActiveAlerts, triggerHealthCheckAction } from '../../../services/firestore/opsService';
import type { DailyHealthReport, OpsAlert } from '../../../types/ops';
import { HealthChecksTab } from './HealthChecksTab';
import { AlertsTab } from './AlertsTab';
import { CostsTab } from './CostsTab';
import { DependenciesTab } from './DependenciesTab';
import { SecurityTab } from './SecurityTab';
import { RemediationTab } from './RemediationTab';
import { CodeDebtTab } from './CodeDebtTab';
import { UnitTestsTab } from './UnitTestsTab';
import { FallbackTab } from './FallbackTab';
import { OpsAgentChat } from './OpsAgentChat';

// ── Design tokens (rose accent, matches spec) ──

const C = {
  text: '#18181B',
  textSec: '#52525B',
  textMuted: '#A1A1AA',
  rose: '#E11D48',
  roseSoft: '#FFF1F2',
  pass: '#059669',
  passBg: '#D1FAE5',
  warn: '#B45309',
  warnBg: '#FEF3C7',
  fail: '#E11D48',
  failBg: '#FFF1F2',
  bg: '#FAFAFA',
  bgCard: '#FFFFFF',
  border: '#E4E4E7',
};

const TABS = [
  { key: 'overview', label: 'Overview', icon: Activity },
  { key: 'health', label: 'Health Checks', icon: Heart },
  { key: 'alerts', label: 'Alerts', icon: AlertTriangle },
  { key: 'costs', label: 'Costs', icon: DollarSign },
  { key: 'deps', label: 'Dependencies', icon: Package },
  { key: 'security', label: 'Security', icon: Shield },
  { key: 'remediation', label: 'Remediation', icon: Wrench },
  { key: 'debt', label: 'Code Debt', icon: Code2 },
  { key: 'tests', label: 'Tests', icon: FlaskConical },
  { key: 'fallback', label: 'Fallback', icon: ArrowDownUp },
] as const;

type TabKey = typeof TABS[number]['key'];

const VALID_TABS = new Set(TABS.map(t => t.key));

export function PlatformHealthDashboard() {
  const [searchParams] = useSearchParams();
  const initialTab = useMemo(() => {
    const param = searchParams.get('tab');
    return param && VALID_TABS.has(param as TabKey) ? (param as TabKey) : 'overview';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [loading, setLoading] = useState(true);
  const [dailyReport, setDailyReport] = useState<DailyHealthReport | null>(null);
  const [activeAlerts, setActiveAlerts] = useState<OpsAlert[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [report, alerts] = await Promise.all([
          getDailyReport(today),
          getActiveAlerts(),
        ]);
        setDailyReport(report);
        setActiveAlerts(alerts);
      } catch (err) {
        console.error('Failed to load ops data:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const alertCount = activeAlerts.length;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{
        fontFamily: "'Instrument Serif', serif",
        fontSize: 24,
        color: C.text,
        marginBottom: 16,
      }}>
        Platform Health
      </h2>

      {/* Tab bar */}
      <div style={{
        display: 'flex',
        gap: 4,
        borderBottom: `1px solid ${C.border}`,
        marginBottom: 24,
        overflowX: 'auto',
      }}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '10px 16px',
                fontSize: 14,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: isActive ? 600 : 400,
                color: isActive ? C.rose : C.textSec,
                background: 'none',
                border: 'none',
                borderBottom: isActive ? `2px solid ${C.rose}` : '2px solid transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                position: 'relative',
              }}
            >
              <Icon size={16} />
              {tab.label}
              {tab.key === 'alerts' && alertCount > 0 && (
                <span style={{
                  background: C.rose,
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  borderRadius: 8,
                  padding: '1px 6px',
                  marginLeft: 4,
                }}>
                  {alertCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.textMuted, fontSize: 14 }}>
          Loading platform health data...
        </div>
      ) : (
        <TabContent tab={activeTab} report={dailyReport} alerts={activeAlerts} onNavigate={setActiveTab} onRefresh={async () => {
          const today = new Date().toISOString().slice(0, 10);
          const [report, alerts] = await Promise.all([getDailyReport(today), getActiveAlerts()]);
          setDailyReport(report);
          setActiveAlerts(alerts);
        }} />
      )}
    </div>
  );
}

// ── Tab Content Router ──

function TabContent({ tab, report, alerts, onNavigate, onRefresh }: {
  tab: TabKey;
  report: DailyHealthReport | null;
  alerts: OpsAlert[];
  onNavigate: (tab: TabKey) => void;
  onRefresh: () => Promise<void>;
}) {
  switch (tab) {
    case 'overview':
      return <OverviewTab report={report} alerts={alerts} onNavigate={onNavigate} onRefresh={onRefresh} />;
    case 'health':
      return <HealthChecksTab />;
    case 'alerts':
      return <AlertsTab />;
    case 'costs':
      return <CostsTab />;
    case 'deps':
      return <DependenciesTab />;
    case 'security':
      return <SecurityTab />;
    case 'remediation':
      return <RemediationTab />;
    case 'debt':
      return <CodeDebtTab />;
    case 'tests':
      return <UnitTestsTab />;
    case 'fallback':
      return <FallbackTab />;
  }
}

// ── Overview Tab ──

function OverviewTab({ report, alerts, onNavigate, onRefresh }: {
  report: DailyHealthReport | null;
  alerts: OpsAlert[];
  onNavigate: (tab: TabKey) => void;
  onRefresh: () => Promise<void>;
}) {
  const [runningCheck, setRunningCheck] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);

  const handleRunNow = async () => {
    setRunningCheck(true);
    setCheckError(null);
    try {
      await triggerHealthCheckAction();
      await onRefresh();
    } catch (err: any) {
      setCheckError(err.message || 'Health check failed');
    } finally {
      setRunningCheck(false);
    }
  };

  if (!report) {
    return (
      <div style={{
        background: C.bgCard,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
        padding: 40,
        textAlign: 'center',
      }}>
        <Activity size={32} style={{ color: C.textMuted, marginBottom: 12 }} />
        <p style={{ fontSize: 16, color: C.text, fontWeight: 600, marginBottom: 8 }}>
          No health data yet
        </p>
        <p style={{ fontSize: 14, color: C.textSec, marginBottom: 16 }}>
          The daily health check hasn't run yet. Click below to run it now, or wait for the automated check at 2:30 AM UTC.
        </p>
        <button
          onClick={handleRunNow}
          disabled={runningCheck}
          style={{
            padding: '10px 24px',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "'DM Sans', sans-serif",
            borderRadius: 4,
            border: 'none',
            cursor: runningCheck ? 'not-allowed' : 'pointer',
            background: runningCheck ? C.border : C.rose,
            color: runningCheck ? C.textMuted : '#fff',
          }}
        >
          {runningCheck ? 'Running Health Check...' : 'Run Health Check Now'}
        </button>
        {checkError && (
          <p style={{ fontSize: 13, color: C.fail, marginTop: 12 }}>{checkError}</p>
        )}
      </div>
    );
  }

  const statusColor = report.overallStatus === 'healthy' ? C.pass
    : report.overallStatus === 'degraded' ? C.warn : C.fail;
  const statusBg = report.overallStatus === 'healthy' ? C.passBg
    : report.overallStatus === 'degraded' ? C.warnBg : C.failBg;

  const p0Count = alerts.filter(a => a.severity === 'P0').length;
  const p1Count = alerts.filter(a => a.severity === 'P1').length;

  const cardStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
    background: C.bgCard,
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    padding: 24,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
    ...extra,
  });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
      {/* Score card → Health Checks */}
      <div onClick={() => onNavigate('health')} style={cardStyle({ display: 'flex', alignItems: 'center', gap: 20 })}
        onMouseEnter={e => (e.currentTarget.style.borderColor = C.rose)}
        onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}
      >
        <ScoreRing score={report.score} color={statusColor} />
        <div>
          <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>Health Score</p>
          <p style={{
            fontSize: 13,
            fontWeight: 600,
            color: statusColor,
            background: statusBg,
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 4,
            marginTop: 4,
          }}>
            {report.overallStatus.toUpperCase()}
          </p>
        </div>
      </div>

      {/* Active alerts → Alerts */}
      <div onClick={() => onNavigate('alerts')} style={cardStyle()}
        onMouseEnter={e => (e.currentTarget.style.borderColor = C.rose)}
        onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}
      >
        <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Active Alerts</p>
        <p style={{ fontSize: 28, fontWeight: 700, color: alerts.length > 0 ? C.fail : C.pass }}>{alerts.length}</p>
        {(p0Count > 0 || p1Count > 0) && (
          <p style={{ fontSize: 13, color: C.textSec, marginTop: 4 }}>
            {p0Count > 0 && <span style={{ color: C.fail, fontWeight: 600 }}>{p0Count} P0</span>}
            {p0Count > 0 && p1Count > 0 && ' / '}
            {p1Count > 0 && <span style={{ color: C.warn, fontWeight: 600 }}>{p1Count} P1</span>}
          </p>
        )}
      </div>

      {/* Costs → Costs */}
      <div onClick={() => onNavigate('costs')} style={cardStyle()}
        onMouseEnter={e => (e.currentTarget.style.borderColor = C.rose)}
        onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}
      >
        <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Today's Cost</p>
        <p style={{ fontSize: 28, fontWeight: 700, color: C.text }}>
          ${(report.checks.costs?.totalUsd ?? 0).toFixed(2)}
        </p>
        <p style={{ fontSize: 13, color: C.textSec, marginTop: 4 }}>
          {report.checks.costs?.utilizationPercent ?? 0}% of daily budget
        </p>
      </div>

      {/* Scheduled Jobs → Health Checks */}
      <div onClick={() => onNavigate('health')} style={cardStyle()}
        onMouseEnter={e => (e.currentTarget.style.borderColor = C.rose)}
        onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}
      >
        <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Scheduled Jobs</p>
        {(() => {
          const jobs = report.checks.scheduledJobs?.jobs || [];
          const failedCount = jobs.filter((j: { ok: boolean }) => !j.ok).length;
          return (
            <>
              <p style={{ fontSize: 28, fontWeight: 700, color: failedCount > 0 ? C.warn : C.pass }}>
                {jobs.length - failedCount}/{jobs.length}
              </p>
              <p style={{ fontSize: 13, color: C.textSec, marginTop: 4 }}>
                {failedCount > 0 ? `${failedCount} job${failedCount > 1 ? 's' : ''} missed` : 'All jobs on schedule'}
              </p>
            </>
          );
        })()}
      </div>

      {/* Event Bus → Security */}
      <div onClick={() => onNavigate('security')} style={cardStyle()}
        onMouseEnter={e => (e.currentTarget.style.borderColor = C.rose)}
        onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}
      >
        <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Event Bus</p>
        <p style={{ fontSize: 28, fontWeight: 700, color: (report.checks.eventBus?.pendingOver24h ?? 0) > 0 ? C.warn : C.pass }}>
          {report.checks.eventBus?.pendingOver24h ?? 0}
        </p>
        <p style={{ fontSize: 13, color: C.textSec, marginTop: 4 }}>
          pending &gt;24h &middot; {report.checks.eventBus?.failedIn24h ?? 0} failed
        </p>
      </div>

      {/* Last Build — Coming Soon */}
      <div style={cardStyle()}>
        <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Last Build</p>
        <span style={{
          display: 'inline-block',
          fontSize: 13,
          fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif",
          color: C.rose,
          background: C.roseSoft,
          padding: '4px 12px',
          borderRadius: 4,
          marginBottom: 12,
        }}>
          Coming Soon
        </span>
        <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.5 }}>
          CI/CD pipeline integration is planned. Build status, deploy history, and failure tracking will appear here once configured.
        </p>
      </div>

      {/* Promise Keeper summary — full width → Security */}
      <div onClick={() => onNavigate('security')} style={cardStyle({ gridColumn: '1 / -1' })}
        onMouseEnter={e => (e.currentTarget.style.borderColor = C.rose)}
        onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}
      >
        <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
          TOS Controls / Promise Keeper
        </p>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <PKStat label="Monitored" value={report.promiseKeeper?.monitored ?? 0} total={(report.promiseKeeper?.monitored ?? 0) + (report.promiseKeeper?.unmonitored ?? 0)} color={C.text} />
          <PKStat label="Healthy" value={report.promiseKeeper?.healthy ?? 0} color={C.pass} />
          <PKStat label="Degraded" value={report.promiseKeeper?.degraded ?? 0} color={C.warn} />
          <PKStat label="Failing" value={report.promiseKeeper?.failing ?? 0} color={C.fail} />
          <PKStat label="Unmonitored" value={report.promiseKeeper?.unmonitored ?? 0} color={C.textMuted} />
        </div>
      </div>

      {/* Ops Agent Chat — full width */}
      <div style={{ gridColumn: '1 / -1' }}>
        <OpsAgentChat />
      </div>
    </div>
  );
}

// ── Shared Components ──

function ScoreRing({ score, color }: { score: number; color: string }) {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <svg width={80} height={80} viewBox="0 0 80 80">
      <circle cx="40" cy="40" r={radius} fill="none" stroke="#E4E4E7" strokeWidth="6" />
      <circle
        cx="40" cy="40" r={radius} fill="none"
        stroke={color} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        transform="rotate(-90 40 40)"
      />
      <text x="40" y="44" textAnchor="middle" fontSize="18" fontWeight="700" fill={color}
        style={{ fontFamily: "'DM Sans', sans-serif" }}>
        {score}
      </text>
    </svg>
  );
}

function PKStat({ label, value, total, color }: {
  label: string; value: number; total?: number; color: string;
}) {
  return (
    <div>
      <p style={{ fontSize: 24, fontWeight: 700, color }}>{value}{total ? `/${total}` : ''}</p>
      <p style={{ fontSize: 13, color: C.textSec }}>{label}</p>
    </div>
  );
}

